import { throttle } from '@goodboydigital/astro';
import p2 from 'p2';
import { Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { PhysicsCollisionGroup, PhysicsShapeType, PhysicsUtils } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { BaseEntity } from './BaseEntity';
import { CharacterEntity } from './CharacterEntity';

export interface PuncherEntityParams
{
    type: string,
    x: number,
    y: number,
    sx: number,
    sy: number,
    r: number,
    img: string,
    props: {
        frequency: number
        step: number
    }
}

export class PuncherEntity extends BaseEntity
{
    public base: Sprite;
    public glove: Sprite;
    public playing: boolean;
    public punching: number;
    public contact: number;
    public params!: PuncherEntityParams;
    public physics: PhysicsComponent;
    public culling: CullingComponent;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.culling = this.addComponent(CullingComponent);

        this.base = Sprite.from(Shared.assets.getTexture('puncher-base'));
        this.base.anchor.set(0.5);
        this.view.addChild(this.base);

        this.glove = Sprite.from(Shared.assets.getTexture('puncher-glove-large'));

        this.glove.anchor.set(0.5);
        this.view.addChild(this.glove);

        this.punching = 0;
        this.contact = 0;
        this.playing = false;
        this.physics.physicsFixedUpdateRunner.add(this as any);
    }

    public levelReset(): void
    {
        this.punching = 0;
        this.contact = 0;
        this.playing = false;
    }

    public build(params: PuncherEntityParams): void
    {
        this.physics.clearParts();
        this.params = params;

        const absx = Math.abs(this.params.sx);
        const absy = Math.abs(this.params.sy);

        const bodyMid = PhysicsUtils.createBody({
            x: this.params.x,
            y: this.params.y + 40,
            mass: 0,
        }, {
            type: PhysicsShapeType.BOX,
            width: 80 * absx,
            height: 80 * absy,
            sensor: true,
            group: PhysicsCollisionGroup.OBJECT,
            mask: PhysicsCollisionGroup.VEHICLE,
        });

        this.physics.viewsToRotate = [this.view];
        this.physics.addParts(bodyMid);
        this.physics.rotate(this.params.r);

        this.view.scale.set(this.params.sx, this.params.sy);
        this.base.y = -40;
        this.glove.x = 0;
        this.gloveHidden();

        this.punching = 0;
        this.contact = 0;

        bodyMid.onBeginContact = (other: p2.Body) => this.evaluateBeginContact(1, other);
        bodyMid.onEndContact = (other: p2.Body) => this.evaluateEndContact(1, other);

        this.culling.updateBounds();
    }

    protected physicsFixedUpdate(): void
    {
        const game = this.scene as Game;
        const time = Math.floor(game.race.time / 1000);
        const frequency = this.params.props.frequency || 4;
        const step = this.params.props.step || 1;

        if (time % frequency === step) this.playPunch();

        if (this.punching && this.contact) game.vehicle.crash();
        this.punching = 0;
    }

    private evaluateBeginContact(index: number, other: p2.Body): void
    {
        const game = this.scene as Game;

        if (!game.vehicle.running) return;

        if (other.physicsComponent?.entity && other.physicsComponent?.entity instanceof CharacterEntity)
        {
            this.contact = 1;
        }
    }

    private evaluateEndContact(index: number, other: p2.Body): void
    {
        if (other.physicsComponent?.entity && other.physicsComponent?.entity instanceof CharacterEntity)
        {
            this.game?.soundSystem.playSfx('puncher');
            this.contact = 0;
        }
    }

    private playPunch(): void
    {
        if (this.playing) return;
        this.playing = true;
        const game = this.scene as Game;

        this.gloveSmall();
        game.physics.timer.dispatchAfter(1, () => this.gloveLarge());
        game.physics.timer.dispatchAfter(1.2, () => this.gloveSmall());
        game.physics.timer.dispatchAfter(1.4, () => this.gloveHidden());
        const approxDist = Math.abs(this.view.x);

        if (approxDist < 1500)
        {
            const volume = 1 - (approxDist / 1500);

            throttle(this.playSfx, 100, this)(volume);
        }
    }

    private playSfx(volume: number)
    {
        const swing = this.game?.soundSystem.playSfx('windmill', volume);

        if (swing) swing.rate = 1.5;
    }

    private gloveSmall(): void
    {
        this.punching = 0;
        this.glove.scale.x = 0.25;
        this.glove.scale.y = 0.25;

        this.game?.tween.to(this.glove, {
            duration: 0.2,
            alpha: 1,
        });
    }

    private gloveLarge(): void
    {
        this.glove.alpha = 1;
        this.punching = 1;

        this.game?.tween.to(this.glove.scale, {
            duration: 0.2,
            x: 1,
            y: 1,
        });
    }

    private gloveHidden(): void
    {
        this.game?.tween.to(this.glove.scale, {
            duration: 0.2,
            x: 0.25,
            y: 0.25,
        });

        this.game?.tween.to(this.glove, {
            duration: 0.2,
            alpha: 0.001,
        });

        this.playing = false;
    }
}

export class PuncherFactory
{
    public static match(data: Record<string, any>): boolean
    {
        return data.type === 'SpriteEntity' && data.img === 'puncher';
    }

    public static create(game: Game, data: Record<string, any>): void
    {
        const entity = game.pool.get(PuncherEntity) as PuncherEntity;

        entity.build(data as PuncherEntityParams);

        game.level.addEntity(entity);
    }
}

